/**
 * Catchball Virtual Participant Config Service
 *
 * DB read/write for catchball_virtual_configs.
 * One config row per cycle; upsert semantics.
 *
 * Not an authority-transition service: this config governs participation
 * visibility and notification routing, not governance state. Direct
 * insert/upsert (not RPC) is appropriate per §2.3a.
 */

import { getSafeSupabaseClient } from './supabaseSync';
import {
  type CatchballVirtualConfig,
  type CatchballVirtualConfigRow,
  type CatchballParticipantRef,
  type CatchballSetupMode,
  rowToVirtualConfig,
} from '../types/prioritization/catchballVirtualConfig';

const TABLE = 'catchball_virtual_configs';

// ── Read ───────────────────────────────────────────────────────────────────

export async function getVirtualConfig(
  cycleId: string
): Promise<CatchballVirtualConfig | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('cycle_id', cycleId)
    .maybeSingle();

  if (error) {
    console.warn('[catchballVirtualConfigService] getVirtualConfig error:', error.message);
    return null;
  }
  if (!data) return null;
  return rowToVirtualConfig(data as CatchballVirtualConfigRow);
}

// ── Write ──────────────────────────────────────────────────────────────────

export interface SaveVirtualConfigInput {
  cycleId: string;
  workspaceId: string;
  setupMode: CatchballSetupMode;
  ballRecipients: CatchballParticipantRef[];
  defaultParticipants: CatchballParticipantRef[];
  currentUserId: string;
}

export async function saveVirtualConfig(
  input: SaveVirtualConfigInput
): Promise<CatchballVirtualConfig | null> {
  const supabase = getSafeSupabaseClient();
  if (!supabase) return null;

  const row = {
    cycle_id: input.cycleId,
    workspace_id: input.workspaceId,
    setup_mode: input.setupMode,
    ball_recipients: input.ballRecipients,
    default_participants: input.defaultParticipants,
    created_by: input.currentUserId,
  };

  const { data, error } = await supabase
    .from(TABLE)
    .upsert(row, { onConflict: 'cycle_id' })
    .select('*')
    .single();

  if (error) {
    console.error('[catchballVirtualConfigService] saveVirtualConfig error:', error.message);
    return null;
  }
  return rowToVirtualConfig(data as CatchballVirtualConfigRow);
}

// ── Group membership resolution ────────────────────────────────────────────

/**
 * Resolves group IDs from a participant list to their member user IDs.
 * Used by notification dispatch to ensure group members are notified.
 *
 * Queries `memberships` (group_id/user_id) — the only real user↔group table.
 * (This previously queried `group_memberships`, which does not exist, and so
 * silently returned [] for every group.)
 */
export async function resolveGroupMemberUserIds(
  groupIds: string[]
): Promise<string[]> {
  if (groupIds.length === 0) return [];
  const supabase = getSafeSupabaseClient();
  if (!supabase) return [];

  try {
    const { data } = await supabase
      .from('memberships')
      .select('user_id')
      .in('group_id', groupIds);

    return (data ?? []).map((r: { user_id: string }) => r.user_id).filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Expands a list of participant refs to concrete user IDs, resolving
 * group memberships along the way.
 */
export async function expandParticipantRefsToUserIds(
  refs: CatchballParticipantRef[],
  excludeUserId?: string
): Promise<string[]> {
  const directUserIds = refs
    .filter((r) => r.type === 'user')
    .map((r) => r.id);

  const groupIds = refs
    .filter((r) => r.type === 'group')
    .map((r) => r.id);

  const groupMemberIds = await resolveGroupMemberUserIds(groupIds);

  const allIds = [...new Set([...directUserIds, ...groupMemberIds])];
  return excludeUserId ? allIds.filter((id) => id !== excludeUserId) : allIds;
}
